import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { FAILURE_REASONS, useBatch, useCompleteBatch } from '@/api/batches'
import type { BatchDetail, CompletionBody } from '@/api/batches'
import { useIdempotencyKey } from '@/api/idempotency'
import { formatGrams, selectClass, slotsByPool } from '@/components/batches/batch-format'
import { FieldError } from '@/components/batches/batch-parts'
import { batchErrorMessage } from '@/components/batches/error-message'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Complete Batch (UI-SCREENS F-07, #129), phone first: numeric keyboards, 44px targets, native
// select. OK parts, failed parts (prefilled expected − OK, #126 Q8), the failure reason (fixed
// list, only while failed > 0), actual grams per color slot prefilled with the estimate and grouped
// under their Pool (#126 Q7), and actual minutes prefilled with the elapsed time. The server
// re-checks all of it and posts the Pool deductions and, for a production Batch, the WIP parts.
export const Route = createFileRoute('/w/$slug/batches/$batchId/complete')({
  component: CompleteBatch,
  staticData: { breadcrumb: 'batches.complete.title' },
})

function CompleteBatch() {
  const { t } = useTranslation()
  const { slug, batchId } = Route.useParams()
  const batch = useBatch(slug, Number(batchId))

  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <Link
        to="/w/$slug/batches/$batchId"
        params={{ slug, batchId }}
        className="text-sm underline-offset-4 hover:underline"
      >
        {t('batches.back')}
      </Link>
      <h1 className="font-heading text-xl font-semibold">
        {t('batches.complete.title')}
        {batch.data ? ` ${batch.data.batch_no}` : ''}
      </h1>

      {batch.isPending ? (
        <LoadingSkeleton variant="form" rows={4} />
      ) : batch.isError || !batch.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : !batch.data.can.complete ? (
        <p role="alert" className="text-sm text-destructive">
          {t('batches.errors.invalidTransition')}
        </p>
      ) : (
        <CompleteForm slug={slug} batch={batch.data} />
      )}
    </section>
  )
}

// RHF would read a numeric key as an array index, so grams are keyed "s<model_slot_id>".
const slotKey = (modelSlotId: number) => `s${modelSlotId}`

const isWhole = (value: string) => /^\d+$/.test(value.trim())
const toWhole = (value: string | undefined) => (value !== undefined && isWhole(value) ? Number(value) : null)

// Minutes since the start, at least 1: the prefill for a live Batch (#126 Q8).
function elapsedMinutes(startedAt: string): number {
  return Math.max(1, Math.round((Date.now() - Date.parse(startedAt)) / 60000))
}

function CompleteForm({ slug, batch }: { slug: string; batch: BatchDetail }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const complete = useCompleteBatch()
  const idempotency = useIdempotencyKey()
  const [failedTouched, setFailedTouched] = useState(false)
  const expected = batch.plates * batch.parts_per_plate
  const wholeNumber = t('batches.complete.wholeNumber')

  const formSchema = z
    .object({
      partsOk: z.string().refine(isWhole, { message: wholeNumber }),
      partsFailed: z.string().refine(isWhole, { message: wholeNumber }),
      failureReason: z.string(),
      actualMinutes: z
        .string()
        .refine((value) => isWhole(value) && Number(value) > 0, { message: t('batches.complete.positiveNumber') }),
      grams: z.record(z.string(), z.string().refine(isWhole, { message: wholeNumber })),
    })
    .superRefine((values, context) => {
      const ok = toWhole(values.partsOk)
      const failed = toWhole(values.partsFailed)
      if (ok !== null && failed !== null && ok + failed === 0) {
        context.addIssue({ code: 'custom', path: ['partsOk'], message: t('batches.complete.nothingPrinted') })
      }
      if (failed !== null && failed > 0 && values.failureReason === '') {
        context.addIssue({ code: 'custom', path: ['failureReason'], message: t('batches.form.required') })
      }
    })
  type FormValues = z.infer<typeof formSchema>

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      partsOk: String(expected),
      partsFailed: '0',
      failureReason: '',
      actualMinutes: String(elapsedMinutes(batch.started_at)),
      grams: Object.fromEntries(batch.slots.map((slot) => [slotKey(slot.model_slot_id), String(slot.est_grams)])),
    },
  })
  const partsFailed = useWatch({ control: form.control, name: 'partsFailed' })
  const failed = toWhole(partsFailed)
  const errors = form.formState.errors

  // Failed follows expected − OK until it is typed in by hand (#126 Q8).
  const onOkChange = (value: string) => {
    const ok = toWhole(value)
    if (!failedTouched && ok !== null) form.setValue('partsFailed', String(Math.max(expected - ok, 0)))
  }

  const onSubmit = form.handleSubmit((values) => {
    const failedCount = Number(values.partsFailed)
    const body: CompletionBody = {
      completion: {
        parts_ok: Number(values.partsOk),
        parts_failed: failedCount,
        failure_reason: failedCount > 0 ? values.failureReason : null,
        actual_minutes: Number(values.actualMinutes),
        slots: batch.slots.map((slot) => ({
          model_slot_id: slot.model_slot_id,
          actual_grams: Number(values.grams[slotKey(slot.model_slot_id)]),
        })),
      },
    }
    complete.mutate(
      { params: { path: { slug, batch_id: batch.id }, header: idempotency.headerFor(body) }, body },
      {
        onSuccess: () => navigate({ to: '/w/$slug/batches/$batchId', params: { slug, batchId: String(batch.id) } }),
        onSettled: (_data, error) => idempotency.settle(error),
      },
    )
  })

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {complete.isError ? batchErrorMessage(t, complete.error) : ''}
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-2">
          <Label htmlFor="partsOk">{t('batches.complete.partsOk')}</Label>
          <Input
            id="partsOk"
            inputMode="numeric"
            className="h-11"
            {...form.register('partsOk', { onChange: (event) => onOkChange(event.target.value) })}
          />
          <FieldError message={errors.partsOk?.message} />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="partsFailed">{t('batches.complete.partsFailed')}</Label>
          <Input
            id="partsFailed"
            inputMode="numeric"
            className="h-11"
            {...form.register('partsFailed', { onChange: () => setFailedTouched(true) })}
          />
          <FieldError message={errors.partsFailed?.message} />
        </div>
      </div>
      <p className="text-sm text-muted-foreground">{t('batches.complete.partsFailedHint', { expected })}</p>

      {failed !== null && failed > 0 ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="failureReason">{t('batches.complete.failureReason')}</Label>
          <select id="failureReason" className={selectClass} {...form.register('failureReason')}>
            <option value="">{t('batches.complete.selectReason')}</option>
            {FAILURE_REASONS.map((reason) => (
              <option key={reason} value={reason}>
                {t(`batches.failureReason.${reason}`)}
              </option>
            ))}
          </select>
          <FieldError message={errors.failureReason?.message} />
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        <h2 className="font-heading text-base font-semibold">{t('batches.complete.grams')}</h2>
        <p className="text-sm text-muted-foreground">{t('batches.complete.gramsHint')}</p>
        {slotsByPool(batch.slots).map((group) => (
          <Card key={group.pool.id}>
            <CardContent className="flex flex-col gap-3 py-4">
              <span className="font-medium">{group.pool.label}</span>
              {group.slots.map((slot) => (
                <div key={slot.model_slot_id} className="flex flex-col gap-2">
                  <Label htmlFor={`grams-${slot.model_slot_id}`}>{slot.label}</Label>
                  <Input
                    id={`grams-${slot.model_slot_id}`}
                    inputMode="numeric"
                    className="h-11"
                    {...form.register(`grams.${slotKey(slot.model_slot_id)}`)}
                  />
                  <span className="text-sm text-muted-foreground">
                    {t('batches.complete.estimate', { grams: formatGrams(slot.est_grams) })}
                  </span>
                  <FieldError message={errors.grams?.[slotKey(slot.model_slot_id)]?.message} />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="actualMinutes">{t('batches.complete.actualMinutes')}</Label>
        <Input id="actualMinutes" inputMode="numeric" className="h-11" {...form.register('actualMinutes')} />
        <p className="text-sm text-muted-foreground">{t('batches.complete.actualMinutesHint')}</p>
        <FieldError message={errors.actualMinutes?.message} />
      </div>

      <p className="text-sm text-muted-foreground">
        {t(batch.purpose === 'production' ? 'batches.complete.wipHint' : 'batches.complete.noWipHint')}
      </p>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="h-11" disabled={complete.isPending}>
          {t(complete.isPending ? 'batches.complete.saving' : 'batches.complete.save')}
        </Button>
        <Button asChild variant="ghost" className="h-11">
          <Link to="/w/$slug/batches/$batchId" params={{ slug, batchId: String(batch.id) }}>
            {t('batches.complete.cancel')}
          </Link>
        </Button>
      </div>
    </form>
  )
}
